import React from 'react'

import { Box, Divider, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material'
import CustomerFeatureSource, { changedAtLabel } from 'features/customers/components/features/CustomerFeatureSource'
import { formatFeatureValue } from 'features/customers/components/features/CustomerFeatureValue'
import type { FeatureChange } from 'features/customers/hooks/useCustomerFeatureChanges'
import type { FeatureRow } from 'features/customers/utils/featureRows'

import { EChangeSource } from '@albacross/types'

type Props = { row: FeatureRow }

// Until DEV-12399 a save replaced the whole override blob, so a reconstructed removal cannot tell a
// deliberate reset from a form dropping a key it never managed.
const removalLabel = (change: FeatureChange) =>
  change.source === EChangeSource.Unknown ? 'Override removed' : 'Back to plan'

const CustomerFeatureHistory = ({ row }: Props) => (
  <Box sx={{ px: 3, py: 2 }}>
    {row.description && (
      <Typography color="text.secondary" sx={{ mb: 1.5 }} variant="body2">
        {row.description}
      </Typography>
    )}

    {row.history.length ? (
      <Table size="small" sx={{ '& td, & th': { border: 0, py: 0.5 }, width: 'auto' }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ color: 'text.secondary', width: 120 }}>Changed to</TableCell>
            <TableCell sx={{ color: 'text.secondary', width: 190 }}>By</TableCell>
            <TableCell sx={{ color: 'text.secondary', width: 170 }}>When</TableCell>
            <TableCell sx={{ color: 'text.secondary', width: 150 }}>Previously</TableCell>
            <TableCell sx={{ color: 'text.secondary' }}>Why</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {row.history.map(change => (
            <TableRow key={change.id}>
              <TableCell>
                <Typography
                  color={change.new_value === null ? 'text.secondary' : 'text.primary'}
                  sx={{ fontWeight: 500 }}
                  variant="body2">
                  {change.new_value === null ? removalLabel(change) : formatFeatureValue(change.new_value, row.name)}
                </Typography>
              </TableCell>
              <TableCell>
                <CustomerFeatureSource change={change} hideDate />
              </TableCell>
              <TableCell>
                <Typography color="text.secondary" variant="body2">
                  {changedAtLabel(change)}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography color="text.secondary" variant="body2">
                  {change.old_value === null || change.old_value === undefined
                    ? 'no override'
                    : formatFeatureValue(change.old_value, row.name)}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography color={change.note ? 'text.primary' : 'text.disabled'} variant="body2">
                  {change.note || '—'}
                </Typography>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    ) : (
      <Typography color="text.secondary" variant="body2">
        Nothing has changed this since we started recording.
      </Typography>
    )}

    <Divider sx={{ my: 1.5 }} />

    <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center', flexWrap: 'wrap' }} useFlexGap>
      <Typography color="text.secondary" variant="body2">
        Plan gives
      </Typography>
      <Typography sx={{ fontWeight: 500 }} variant="body2">
        {formatFeatureValue(row.planValue, row.name)}
      </Typography>
      {row.overridden && (
        <Typography color="text.secondary" variant="body2">
          · a reset returns to this
        </Typography>
      )}
    </Stack>
  </Box>
)

export default CustomerFeatureHistory
